<template>
    <section
        v-if="computedCards"
        class="hero-banner"
        :class="{
            [display.type]: true,
            [$cms.computedBackgroundClass(display, layout?.name)]: true,
            'no-image': display.type !== 'overlay' && computedCards.length === 1 && !computedCards[0].image,
            'many': computedCards.length > 1,
            [`align-${computedAlignment}`]: true,
        }"
    >
        <div class="inner">
            <div
                v-for="item, key in computedCards"
                :key="`item-${key}`"
                class="card"
            >
                <div class="text-overlay">
                    <div class="text-content" :class="{[`align-${computedAlignment}`]: true}">
                        <p v-if="item.byline" class="byline h6">{{ item.byline }}</p>
                        <p v-else-if="item.products" class="byline h6">{{ $cms.productArticle(item.products).attributes.short_description }}</p>
                        <h2
                            v-if="item.title"
                            :class="{
                                'h1': computedCards.length === 1,
                                'h2': display.type === 'overlay' && computedCards.length > 1,
                            }"
                        >
                            {{ item.title }}
                        </h2>
                        <h2
                            v-else-if="item.products"
                            :class="{
                                'h1': computedCards.length === 1,
                                'h2': display.type === 'overlay' && computedCards.length > 1,
                            }"
                        >
                            {{ $cms.productArticle(item.products).attributes.product_name }}
                        </h2>
                        <DSCFText
                            v-if="item.text"
                            :content="item.text"
                        />
                        <DSPrice
                            v-if="item.products"
                            :products="item.products"
                            @add-product="addProduct"
                        />
                        <div v-if="(item.links && item.links.length > 0) || $slots.buttons" class="buttons">
                            <slot name="buttons">
                                <NuxtLink
                                    v-for="link, lKey in item.links.filter(link => link.title)"
                                    :key="`link-${lKey}`"
                                    v-bind="$cms.parseLink(link)"
                                    :class="resolveLinkClass(lKey)"
                                >
                                    <span>{{ link.title }}</span>
                                </NuxtLink>
                            </slot>
                        </div>
                    </div>
                </div>
                <DSFrontifyAsset
                    v-if="item.image"
                    fill="cover"
                    :asset="item.image"
                    :class="computedShadow"
                />
            </div>
        </div>
    </section>
</template>
<script setup>
import { useNuxtApp, inject, computed } from '#imports';
const { $bp, $cms } = useNuxtApp();

const props = defineProps({
    contentType: {
        type: [String, null],
        required: false,
        default: 'heroBanner',
    },
    display: {
        type: Object,
        required: false,
        default: () => ({
            component: 'hero',
            type: 'horizontal',
            alignment: 'left',
            background: 'none',
        }),
    },
    createdAt: {
        type: String,
        required: false,
        default: '',
    },
    updatedAt: {
        type: String,
        required: false,
        default: '',
    },
    isSchedulerEnabled: {
        type: Boolean,
        required: false,
        default: false,
    },
    internal: {
        type: [String, null],
        required: false,
        default: 'Hero banner',
    },
    title: {
        type: String,
        required: false,
        default: '',
    },
    cards: {
        type: Array,
        required: false,
        default: () => [],
    },
});

const layout = inject('layout');

const emits = defineEmits(['openform', 'addProduct']);

const computedCards = computed(() => {
    if (props.display.type === 'overlay') {
        return props.cards.slice(0, 2);
    }

    return props.cards.slice(0, 1);
});

const computedAlignment = computed(() => {
    if (!props.display.alignment) return 'bottomleft';
    if (props.display.alignment === 'center') return 'bottomcenter';
    if (props.display.alignment === 'left') return 'bottomleft';

    return props.display.alignment;
});

const computedShadow = computed(() => {
    if (!computedCards.value[0].title) return 'no-darken';
    if (!props.display.alignment) return;
    if (['topleft', 'topcenter', 'topright'].includes(props.display.alignment)) return 'shadow-top';
    if (['middleleft', 'middlecenter', 'middleright'].includes(props.display.alignment)) return 'shadow-middle';

    return null;
});

const backgroundColor = computed(() => {
    if (props.display.background && props.display.background !== 'none') {
        return props.display.background;
    }

    return null;
});

function addProduct(product) {
    emits('addProduct', product);
    $cms.addProduct(product);
}

function resolveLinkClass(key) {
    return [
        'ds-btn',
        `ds-btn--${import.meta.client && $bp.bps.isSmallDesktop ? 'large' : 'small'}`,
        `ds-btn--${key === 0 ? 'primary' : 'secondary'}`,
    ];
}
</script>
<script>
export default {
    name: 'CLHero',
};
</script>
<style scoped>
.hero-banner.bg-primary {
  background: var(--color-primary-500);
}
.hero-banner.bg-secondary {
  background: var(--color-secondary-500);
}
.hero-banner.bg-tertiary {
  background: var(--color-tertiary-500);
}
.hero-banner.fg-light .h1,
.hero-banner.fg-light .h2,
.hero-banner.fg-light .h3,
.hero-banner.fg-light .h4,
.hero-banner.fg-light .h5,
.hero-banner.fg-light .h6,
.hero-banner.fg-light .rich-text,
.hero-banner.fg-light .byline {
  color: var(--color-base-white);
}
.hero-banner.fg-light .ds-btn--primary {
  background: var(--color-secondary-500);
  color: var(--color-base-black);
}
.hero-banner.fg-light .ds-btn--primary:hover {
  background: var(--color-base-white);
  color: var(--color-base-black);
}
.hero-banner.fg-light .ds-btn--secondary {
  background: none;
  color: var(--color-base-white);
  border-color: var(--color-base-white);
}
.hero-banner.fg-light .ds-btn--secondary:hover {
  background: var(--color-base-white);
  color: var(--color-base-black);
}
.hero-banner {
  width: 100%;
  max-width: var(--desktop-max-width);
}
.hero-banner:last-child {
  margin-bottom: 0;
}
.hero-banner .inner .card .text-content {
  padding-top: 1rem;
  padding-bottom: 1rem;
}
.hero-banner.overlay, .hero-banner.fullscreen {
  display: grid;
  max-width: var(--desktop-max-width);
  width: 100%;
  margin: 0 auto;
}
.hero-banner.overlay .inner, .hero-banner.fullscreen .inner {
  max-width: var(--page-max-width);
  margin: 0 auto;
  padding-left: 1.5rem;
  width: 100%;
  padding-right: 1.5rem;
}
.hero-banner.overlay .inner, .hero-banner.fullscreen .inner {
  max-width: 100%;
  padding-inline: 0;
}
.hero-banner.overlay .inner .card, .hero-banner.fullscreen .inner .card {
  display: grid;
  min-height: 280px;
}
.hero-banner.overlay .inner .card > *, .hero-banner.fullscreen .inner .card > * {
  grid-column-start: 1;
  grid-row-start: 1;
}
.hero-banner.overlay .inner .text-overlay, .hero-banner.fullscreen .inner .text-overlay {
  position: relative;
  z-index: 3;
  pointer-events: none;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr 1fr 1fr;
  grid-column-start: 1;
  grid-row-start: 1;
  box-sizing: border-box;
  padding: 1.5rem;
}
.hero-banner.overlay .inner .text-overlay > *, .hero-banner.fullscreen .inner .text-overlay > * {
  pointer-events: auto;
}
.hero-banner.overlay .inner .text-overlay .text-content h2,
.hero-banner.overlay .inner .text-overlay .text-content p,
.hero-banner.overlay .inner .text-overlay .text-content .rich-text, .hero-banner.fullscreen .inner .text-overlay .text-content h2,
.hero-banner.fullscreen .inner .text-overlay .text-content p,
.hero-banner.fullscreen .inner .text-overlay .text-content .rich-text {
  color: var(--color-base-white);
}
.hero-banner.overlay .inner .text-overlay .text-content > .buttons, .hero-banner.fullscreen .inner .text-overlay .text-content > .buttons {
  margin-top: 1.5rem;
  margin-bottom: -1rem;
  position: relative;
  z-index: 3;
  pointer-events: auto;
}
.hero-banner.overlay .inner .text-overlay .text-content > .buttons > *, .hero-banner.fullscreen .inner .text-overlay .text-content > .buttons > * {
  margin-bottom: 1rem;
}
.hero-banner.overlay .inner .text-overlay .text-content > .buttons > :not(:last-child), .hero-banner.fullscreen .inner .text-overlay .text-content > .buttons > :not(:last-child) {
  margin-right: 1.5rem;
}
.hero-banner.overlay .inner .align-topleft, .hero-banner.fullscreen .inner .align-topleft {
  grid-row-start: 1;
  justify-self: left;
  align-self: center;
}
.hero-banner.overlay .inner .align-topleft *, .hero-banner.fullscreen .inner .align-topleft * {
  text-align: left;
}
.hero-banner.overlay .inner .align-topcenter, .hero-banner.fullscreen .inner .align-topcenter {
  grid-row-start: 1;
  justify-self: center;
  align-self: center;
}
.hero-banner.overlay .inner .align-topcenter *, .hero-banner.fullscreen .inner .align-topcenter * {
  text-align: center;
}
.hero-banner.overlay .inner .align-topright, .hero-banner.fullscreen .inner .align-topright {
  grid-row-start: 1;
  justify-self: right;
  align-self: center;
}
.hero-banner.overlay .inner .align-topright *, .hero-banner.fullscreen .inner .align-topright * {
  text-align: right;
}
.hero-banner.overlay .inner .align-middleleft, .hero-banner.fullscreen .inner .align-middleleft {
  grid-row-start: 2;
  justify-self: left;
  align-self: center;
}
.hero-banner.overlay .inner .align-middleleft *, .hero-banner.fullscreen .inner .align-middleleft * {
  text-align: left;
}
.hero-banner.overlay .inner .align-middlecenter, .hero-banner.fullscreen .inner .align-middlecenter {
  grid-row-start: 2;
  justify-self: center;
  align-self: center;
}
.hero-banner.overlay .inner .align-middlecenter *, .hero-banner.fullscreen .inner .align-middlecenter * {
  text-align: center;
}
.hero-banner.overlay .inner .align-middleright, .hero-banner.fullscreen .inner .align-middleright {
  grid-row-start: 2;
  justify-self: right;
  align-self: center;
}
.hero-banner.overlay .inner .align-middleright *, .hero-banner.fullscreen .inner .align-middleright * {
  text-align: right;
}
.hero-banner.overlay .inner .align-bottomleft, .hero-banner.fullscreen .inner .align-bottomleft {
  grid-row-start: 3;
  justify-self: left;
  align-self: center;
}
.hero-banner.overlay .inner .align-bottomleft *, .hero-banner.fullscreen .inner .align-bottomleft * {
  text-align: left;
}
.hero-banner.overlay .inner .align-bottomcenter, .hero-banner.fullscreen .inner .align-bottomcenter {
  grid-row-start: 3;
  justify-self: center;
  align-self: center;
}
.hero-banner.overlay .inner .align-bottomcenter *, .hero-banner.fullscreen .inner .align-bottomcenter * {
  text-align: center;
}
.hero-banner.overlay .inner .align-bottomright, .hero-banner.fullscreen .inner .align-bottomright {
  grid-row-start: 3;
  justify-self: right;
  align-self: center;
}
.hero-banner.overlay .inner .align-bottomright *, .hero-banner.fullscreen .inner .align-bottomright * {
  text-align: right;
}
.hero-banner.overlay .inner .image,
.hero-banner.overlay .inner .video, .hero-banner.fullscreen .inner .image,
.hero-banner.fullscreen .inner .video {
  padding-top: 38.8888%;
}
.hero-banner.overlay .inner .video, .hero-banner.fullscreen .inner .video {
  padding-top: 45%;
}
@media (max-width: 1199px) {
  .hero-banner.overlay .inner .text-content, .hero-banner.fullscreen .inner .text-content {
    padding-inline: 1.5rem;
  }
  .hero-banner.overlay .inner .image,
  .hero-banner.overlay .inner .video, .hero-banner.fullscreen .inner .image,
  .hero-banner.fullscreen .inner .video {
    padding-top: 65%;
  }
}
@media (max-width: 767px) {
  .hero-banner.overlay .inner .image,
  .hero-banner.overlay .inner .video, .hero-banner.fullscreen .inner .image,
  .hero-banner.fullscreen .inner .video {
    padding-top: 140%;
  }
  .hero-banner.overlay .inner .buttons > *, .hero-banner.fullscreen .inner .buttons > * {
    width: calc(100% - 1.5rem);
    justify-content: center;
  }
  .hero-banner.overlay .inner .buttons > *:not(:last-child), .hero-banner.fullscreen .inner .buttons > *:not(:last-child) {
    margin-bottom: 1rem;
  }
}
.hero-banner.overlay.many .inner, .hero-banner.fullscreen.many .inner {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
}
.hero-banner.overlay.many .image,
.hero-banner.overlay.many .video, .hero-banner.fullscreen.many .image,
.hero-banner.fullscreen.many .video {
  padding-top: 75%;
}
@media (max-width: 1199px) {
  .hero-banner.overlay.many .image,
  .hero-banner.overlay.many .video, .hero-banner.fullscreen.many .image,
  .hero-banner.fullscreen.many .video {
    padding-top: 90%;
  }
}
@media (max-width: 767px) {
  .hero-banner.overlay.many .inner, .hero-banner.fullscreen.many .inner {
    grid-template-columns: 1fr;
  }
  .hero-banner.overlay.many .image,
  .hero-banner.overlay.many .video, .hero-banner.fullscreen.many .image,
  .hero-banner.fullscreen.many .video {
    padding-top: 82%;
  }
}
.hero-banner.fullscreen {
  margin-left: 0;
  margin-bottom: 2rem;
}
.hero-banner.fullscreen .inner {
  height: 100%;
}
.hero-banner.fullscreen .inner .card {
  height: 100%;
  width: 100dvw;
}
.hero-banner.fullscreen .inner .card .image {
  min-height: 90dvh;
}
@media (max-width: 767px) {
  .hero-banner.fullscreen .inner .card {
    height: auto;
  }
  .hero-banner.fullscreen .inner .card .image {
    padding-top: 175%;
  }
}
.hero-banner.overlay {
  margin-bottom: 2rem;
}
.hero-banner.horizontal {
  display: grid;
  max-width: var(--desktop-max-width);
  width: 100%;
  margin: 0 auto;
}
.hero-banner.horizontal .inner {
  justify-self: end;
  padding-left: 1.5rem;
  width: 100%;
}
.hero-banner.horizontal .inner .card {
  display: grid;
  grid-template-columns: auto 57.6%;
}
.hero-banner.horizontal .inner .card .text-overlay {
  display: contents;
}
.hero-banner.horizontal .inner .text-content {
  justify-self: stretch;
  align-self: center;
  padding-right: 3.75rem;
}
.hero-banner.horizontal .inner .text-content > .text {
  font-size: 1.125rem;
}
.hero-banner.horizontal .inner .text-content .h1 {
  margin-top: 1rem;
  margin-bottom: 1rem;
}
.hero-banner.horizontal .inner .buttons {
  margin-top: 1.5rem;
}
.hero-banner.horizontal .inner .buttons > *:not(:first-child) {
  margin-top: 0.5rem;
}
.hero-banner.horizontal .inner .buttons > :not(:last-child) {
  margin-right: 1.5rem;
}
@media (max-width: 991px) {
  .hero-banner.horizontal .inner {
    padding-left: 0;
  }
  .hero-banner.horizontal .inner .card {
    grid-template-columns: 100%;
  }
  .hero-banner.horizontal .inner .text-content {
    order: 1;
    padding: 3rem 1.5rem;
  }
}
.hero-banner.horizontal .image,
.hero-banner.horizontal .video {
  padding-top: 66%;
}
.hero-banner.vertical {
  display: grid;
  max-width: var(--desktop-max-width);
  width: 100%;
  margin: 0 auto;
}
.hero-banner.vertical .inner {
  max-width: var(--page-max-width);
  margin: 0 auto;
  padding-left: 1.5rem;
  width: 100%;
  padding-right: 1.5rem;
}
.hero-banner.vertical .inner {
  padding-inline: 0;
  max-width: 100%;
}
.hero-banner.vertical .inner .text-content {
  max-width: var(--page-max-width);
  margin: 0 auto;
  padding: 3rem 1.5rem;
}
.hero-banner.vertical .inner .text-content > .buttons {
  margin-top: 1rem;
}
.hero-banner.vertical .inner .text-content > .buttons > * {
  margin-top: 0.5rem;
}
.hero-banner.vertical .inner .text-content > .buttons > :not(:last-child) {
  margin-right: 1.5rem;
}
.hero-banner.vertical .image,
.hero-banner.vertical .video {
  position: relative;
  padding-top: 38.8888%;
}
@media (max-width: 991px) {
  .hero-banner.vertical .inner {
    padding: 0;
  }
}
@media (max-width: 991px) {
  .hero-banner.horizontal .image, .hero-banner.horizontal .video, .hero-banner.vertical .image, .hero-banner.vertical .video {
    margin-inline: 1.5rem;
    width: calc(100% - 3rem);
    padding-top: 90%;
  }
}
.hero-banner.no-image {
  display: grid;
  max-width: var(--desktop-max-width);
  width: 100%;
  margin: 0 auto;
}
.hero-banner.no-image .inner {
  max-width: var(--page-max-width);
  margin: 0 auto;
  padding-left: 1.5rem;
  width: 100%;
  padding-right: 1.5rem;
}
.hero-banner.no-image .inner .card {
  grid-template-columns: 1fr;
}
.hero-banner.no-image .inner .card .text-content {
  padding-top: 4rem;
  padding-bottom: 4rem;
}
.hero-banner:not(:first-child) {
  margin-top: 2rem;
}
.hero-banner.align-bottomcenter .inner .text-content {
  justify-self: center;
  text-align: center;
}

.bg-custom {
  background-color: v-bind("backgroundColor");
}
</style>
